// METIS onboarding: pre-populate today's EMPTY agenda from the user's KYRS tasks,
// highest KYRS priority first. Same score + "All active tasks" filter KYRS uses
// (see v10/orb/widgets/adapters/kyrs.ts). Idempotent: does nothing if today
// already has any agenda row, and never duplicates a task_id. Writes are awaited.
import { supabase } from '../../lib/supabase';
import { calculateTaskPriority, parseETCToMinutes, type Group, type ValueSetting } from '../../lib/autoSchedule';

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const genId = () => Math.random().toString(36).substring(2, 9);
const MAX_ROWS = 7; // METIS shows seven blank rows

export async function prefillAgendaFromKyrs(userId: string, limit = MAX_ROWS): Promise<{ created: number; error?: string }> {
  try {
    const d = new Date();
    const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

    const existing = await supabase.from('agenda_items').select('id, task_id').eq('user_id', userId).eq('date', date);
    if (existing.error) return { created: 0, error: existing.error.message };
    if ((existing.data ?? []).length > 0) return { created: 0 };

    const [listsRes, tasksRes, setRes] = await Promise.all([
      supabase.from('task_lists').select('id, include_in_all_tasks_view, category_settings').eq('user_id', userId),
      supabase.from('tasks').select('*').eq('user_id', userId).eq('done', false).limit(1000),
      supabase.from('user_settings').select('groups, value_settings, unscheduled_task_priority_bonus').eq('user_id', userId).maybeSingle(),
    ]);
    if (tasksRes.error) return { created: 0, error: tasksRes.error.message };

    const lists = new Map<string, { inAll: boolean; cats: Record<string, { includeInAllTasks?: boolean }> }>();
    for (const l of (listsRes.data ?? []) as any[]) {
      lists.set(l.id, { inAll: l.include_in_all_tasks_view !== false, cats: l.category_settings ?? {} });
    }
    const s: any = setRes.data ?? {};
    const groups: Group[] = Array.isArray(s.groups) ? s.groups : [];
    const values: ValueSetting[] = Array.isArray(s.value_settings) ? s.value_settings : [];
    const bonus = typeof s.unscheduled_task_priority_bonus === 'number' ? s.unscheduled_task_priority_bonus : 0;

    const ranked: { r: any; score: number; order: number }[] = [];
    for (const r of (tasksRes.data ?? []) as any[]) {
      const list = lists.get(r.list_id);
      if (!list || !list.inAll) continue;
      if (r.tabled && !r.category) continue;
      if (r.category && (list.cats[r.category]?.includeInAllTasks ?? true) === false) continue;
      if (!r.title) continue;
      const score = calculateTaskPriority(
        {
          groupId: r.group_id ?? null,
          sg1Id: r.sg1_id ?? null,
          sg2Id: r.sg2_id ?? null,
          valueSelections: (r.value_selections ?? {}) as Record<string, string>,
          scheduled: r.scheduled ?? null,
          priorityAdd: r.priority_add ?? null,
        },
        groups,
        values,
        bonus,
      );
      ranked.push({ r, score, order: typeof r.order === 'number' ? r.order : Number.MAX_SAFE_INTEGER });
    }
    ranked.sort((a, b) => b.score - a.score || a.order - b.order);

    const seen = new Set<string>();
    const rows: Record<string, unknown>[] = [];
    for (const { r } of ranked) {
      if (rows.length >= limit) break;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      const mins = parseETCToMinutes(r.etc ?? null);
      rows.push({
        user_id: userId,
        date,
        item_id: genId(),
        completed: false,
        text: r.title,
        time: '',
        duration: mins != null ? String(Math.round(mins)) : '',
        sort_order: rows.length,
        nails: [],
        design: null,
        task_id: r.id,
        sector: r.sector ?? null,
        design_id: r.design_id ?? null,
        aim_ref: r.aim_ref ?? null,
        updated_at: new Date().toISOString(),
      });
    }
    if (rows.length === 0) return { created: 0 };
    const ins = await supabase.from('agenda_items').insert(rows);
    if (ins.error) return { created: 0, error: ins.error.message };
    return { created: rows.length };
  } catch (e: any) {
    return { created: 0, error: e?.message ?? String(e) };
  }
}
